/* eslint-disable no-unused-vars */
import React, {useState, useEffect, useCallback, useMemo} from 'react'
import {useNavigate, useLocation, useParams} from 'react-router-dom'
import {useIntl} from 'react-intl'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import {TaskState} from '../../../helpers'
import {KTCard, KTCardBody, Select, Loading, Datepicker} from '../../../components'

import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {taskUpdate, taskGetId} from '../../../redux/slices/workplan.slice'
import {staffGetAll, allCompanyLookup} from '../../../redux/slices/lookup.slice'
import {useMemoizedBreadcrumbs, useMemoizedPageUrl, useMemoizedValidationSchema} from '../../../utils/performanceOptimizations'

function Edit() {
  const intl = useIntl()
  const {id} = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const pageUrl = useMemoizedPageUrl(location)
  const dispatch = useDispatch()

  const selectLookupData = useMemo(
    () => (state) => ({
      staffLookup: state.lookup.staffLookup,
      companyLookup: state.lookup.companyLookup,
      loading: state.lookup.loading,
    }),
    []
  )

  const selectWorkplanData = useMemo(
    () => (state) => ({
      isRedirect: state.workplan.isRedirect,
      detail: state.workplan.detail,
      loading: state.workplan.loading,
    }),
    []
  )

  const {staffLookup, loading, companyLookup} = useSelector(selectLookupData)
  const {isRedirect, detail, loading: updateLoading} = useSelector(selectWorkplanData)

  useEffect(() => {
    dispatch(staffGetAll())
    dispatch(allCompanyLookup())
    dispatch(taskGetId(id))
  }, [id, dispatch])

  const validSchema = useMemoizedValidationSchema(
    () =>
      Yup.object().shape({
        Name: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
        TaskStateId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
        ProjectId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
        ConstructionSiteId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
        BlockId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
      }),
    [intl]
  )

  const [formValues] = useState({
    Id: id,
    Name: '',
    StartDate: new Date(),
    EndDate: new Date(),
    TaskStateId: null,
    RootTaskId: null,
    Duration: 0,
    StaffId: '',
    Description: '',
    ProjectId: null,
    CompletionRate: 0,
    PlotId: null,
    ConstructionSiteId: '',
    BlockId: '',
    TotalWork: 0,
    TotalPersonDay: 0,
    CompanyId: null,
  })

  const handleSubmit = useCallback(
    async (values, {setSubmitting}) => {
      setSubmitting(true)
      const result = await dispatch(taskUpdate(values))
      if (taskUpdate.rejected.match(result)) {
        setSubmitting(false)
      }
    },
    [dispatch]
  )

  const formik = useFormik({
    initialValues: formValues,
    validationSchema: validSchema,
    onSubmit: handleSubmit,
  })

  useEffect(() => {
    for (const property in detail) {
      const propVal = property.charAt(0).toUpperCase() + property.slice(1)
      for (const values in formValues) {
        if (values === propVal) {
          formik.setFieldValue(
            propVal,
            property === 'startDate' || property === 'endDate'
              ? new Date(detail[property])
              : detail[property]
          )
        }
      }
    }
  }, [detail])

  const breadcrumbs = useMemoizedBreadcrumbs(
    () => [
      {
        title: intl.formatMessage({id: 'BC.TASK'}),
        path: '/workplan/task',
        isSeparator: false,
        isActive: false,
      },
    ],
    [intl]
  )

  useEffect(() => {
    if (isRedirect) navigate(`/${pageUrl[1]}/${pageUrl[2]}/detail/${id}`)
  }, [isRedirect, navigate, pageUrl, id])

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>{intl.formatMessage({id: 'BC.TASK.EDIT'})}</PageTitle>
      <KTCard>
        <KTCardBody>
          <form
            className='form'
            onKeyDown={(e) => {
              if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') e.preventDefault()
            }}
            onSubmit={formik.handleSubmit}
            noValidate
          >
            <div className='row mb-6'>
              <label className='col-lg-3 required fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'TASK.CREATE.NAME'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <input
                  type='text'
                  className='form-control form-control-solid mb-3 mb-lg-0'
                  autoComplete='off'
                  disabled={formik.isSubmitting}
                  {...formik.getFieldProps('Name')}
                />
                {formik.touched.Name && formik.errors.Name && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.Name}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 required fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'TASK.CREATE.TASKSTATEID'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <Select
                  data={TaskState}
                  required
                  {...formik.getFieldProps('TaskStateId')}
                  onChange={(item) => formik.setFieldValue('TaskStateId', item.value)}
                  textType={'intl'}
                />
                {formik.touched.TaskStateId && formik.errors.TaskStateId && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.TaskStateId}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 required fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'TASK.CREATE.PROJECTID'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <span className='fw-bold fs-6'>{detail?.projectName}</span>
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 required fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'TASK.CREATE.CONSTRUCTIONSITE'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <span className='fw-bold fs-6'>{detail?.constructionSiteName}</span>
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 required fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'TASK.CREATE.BLOCK'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <span className='fw-bold fs-6'>{detail?.blockName}</span>
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'TASK.CREATE.PLOTID'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <span className='fw-bold fs-6'>{detail?.plotName}</span>
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'TASK.CREATE.COMPANYNAME'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <Select
                  data={companyLookup}
                  {...formik.getFieldProps('CompanyId')}
                  onChange={(item) => formik.setFieldValue('CompanyId', Number(item?.value))}
                />
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'TASK.CREATE.STARTDATE'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <Datepicker
                  {...formik.getFieldProps('StartDate')}
                  onChange={([date]) => formik.setFieldValue('StartDate', date)}
                />
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'TASK.CREATE.ENDDATE'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <Datepicker
                  {...formik.getFieldProps('EndDate')}
                  onChange={([date]) => formik.setFieldValue('EndDate', date)}
                />
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'TASK.CREATE.STAFFID'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <Select
                  data={staffLookup}
                  {...formik.getFieldProps('StaffId')}
                  onChange={(item) => formik.setFieldValue('StaffId', Number(item?.value))}
                />
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'TASK.CREATE.DESCRIPTION'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <textarea
                  className='form-control form-control-solid mb-3 mb-lg-0'
                  autoComplete='off'
                  disabled={formik.isSubmitting}
                  rows={4}
                  {...formik.getFieldProps('Description')}
                />
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'TASK.CREATE.DURATION'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <input
                  type='number'
                  className='form-control form-control-solid mb-3 mb-lg-0'
                  autoComplete='off'
                  disabled={formik.isSubmitting}
                  {...formik.getFieldProps('Duration')}
                  onFocus={(event) => event.target.select()}
                />
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'TASK.CREATE.TOTALPERSONDAY'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <input
                  type='number'
                  className='form-control form-control-solid mb-3 mb-lg-0'
                  autoComplete='off'
                  disabled={formik.isSubmitting}
                  {...formik.getFieldProps('TotalPersonDay')}
                  onFocus={(event) => event.target.select()}
                />
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'TASK.CREATE.TOTALWORK'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <input
                  type='number'
                  className='form-control form-control-solid mb-3 mb-lg-0'
                  autoComplete='off'
                  disabled={formik.isSubmitting}
                  {...formik.getFieldProps('TotalWork')}
                  onFocus={(event) => event.target.select()}
                />
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'TASK.CREATE.COMPLETIONRATE'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <input
                  type='number'
                  className='form-control form-control-solid mb-3 mb-lg-0'
                  autoComplete='off'
                  disabled={formik.isSubmitting}
                  {...formik.getFieldProps('CompletionRate')}
                  onFocus={(event) => event.target.select()}
                />
              </div>
            </div>

            <div className='row pt-15'>
              <div className='col-lg-9 fv-row fv-plugins-icon-container d-flex gap-3 justify-content-center'>
                <button
                  type='button'
                  className='btn btn-light'
                  onClick={() => navigate(`/${pageUrl[1]}/${pageUrl[2]}/detail/${id}`)}
                >
                  {intl.formatMessage({id: 'BUTTON.CANCEL'})}
                </button>
                <button
                  type='submit'
                  className='btn btn-primary'
                  data-kt-users-modal-action='submit'
                  disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
                >
                  <span className='indicator-label'>
                    {intl.formatMessage({id: 'BUTTON.SUBMIT'})}
                  </span>
                  {formik.isSubmitting && (
                    <span className='indicator-progress'>
                      {intl.formatMessage({id: 'PLEASE'})}
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                  )}
                </button>
              </div>
            </div>
          </form>
        </KTCardBody>
      </KTCard>

      {(loading || updateLoading) && <Loading />}
    </>
  )
}
export {Edit}
